import { Box, Group, Modal, SimpleGrid, Slider, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { THRESHOLD_OPTIONS } from '@repo/common/constants';
import { Button, Icon, NumberInput, Select, Text } from '@repo/foundations';
import { useEffect, useState } from 'react';
import type { Monitor } from '../../api';
import { useBulkUpdateMonitors } from '../../api';

const AUTO_THRESHOLDS_MESSAGE =
	"Automatic thresholds use your monitor's historical data to adjust limits, improving accuracy progressively over time.";
const SENSITIVITY_HELPER =
	'If adjusted, higher sensitivity catches smaller details, while lower sensitivity focuses on bigger changes.';

interface BulkEditThresholdsModalProps {
	opened: boolean;
	onClose: () => void;
	monitors: Monitor[];
}

type ThresholdMode = 'automatic' | 'manual';

export default function BulkEditThresholdsModal({
	opened,
	onClose,
	monitors,
}: BulkEditThresholdsModalProps) {
	const { mutateAsync: bulkUpdateMonitors } = useBulkUpdateMonitors({});
	const [thresholdMode, setThresholdMode] =
		useState<ThresholdMode>('automatic');
	const [sensitivity, setSensitivity] = useState<number>(5);
	const [manualMin, setManualMin] = useState<number | undefined>(undefined);
	const [manualMax, setManualMax] = useState<number | undefined>(undefined);
	const [submitting, setSubmitting] = useState(false);
	const [minError, setMinError] = useState<string | undefined>(undefined);
	const [maxError, setMaxError] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (opened) {
			setThresholdMode('automatic');
			setSensitivity(5);
			setManualMin(undefined);
			setManualMax(undefined);
			setMinError(undefined);
			setMaxError(undefined);
		}
	}, [opened]);

	const handleThresholdModeChange = (val: string | null) => {
		setThresholdMode(val as ThresholdMode);
	};

	const handleManualMinChange = (val: number | undefined) => {
		setManualMin(val);
		setMinError(undefined);
		if (manualMax !== undefined && val !== undefined && manualMax < val) {
			setMaxError(
				'The maximum value should be greater than the minimum value.'
			);
		} else {
			setMaxError(undefined);
		}
	};
	const handleManualMaxChange = (val: number | undefined) => {
		setManualMax(val);
		setMaxError(undefined);
		if (manualMin !== undefined && val !== undefined && val < manualMin) {
			setMaxError(
				'The maximum value should be greater than the minimum value.'
			);
		} else {
			setMaxError(undefined);
		}
	};

	const handleSubmit = async () => {
		setSubmitting(true);
		try {
			// Build updates object for bulk update
			const updates: Record<string, Record<string, any>> = {};
			monitors.forEach((monitor) => {
				updates[monitor.id] =
					(thresholdMode as string) === 'automatic'
						? {
								condition_auto_sensitivity: sensitivity,
								condition_manual_min: null,
								condition_manual_max: null,
							}
						: {
								condition_auto_sensitivity: null,
								condition_manual_min: manualMin,
								condition_manual_max: manualMax,
							};
			});

			await bulkUpdateMonitors({
				body: {
					updates,
				},
			});

			showNotification({
				title: 'Thresholds updated',
				message: `Thresholds updated for ${monitors.length} monitor${monitors.length > 1 ? 's' : ''}.`,
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
			onClose();
		} catch (e) {
			showNotification({
				title: 'Error',
				message: 'Failed to update thresholds for selected monitors.',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title="Edit Thresholds"
			closeOnEscape
			closeOnClickOutside
			size="lg"
			maw={700}
		>
			<Stack spacing="lg">
				<Select
					label="Threshold"
					value={thresholdMode}
					onChange={handleThresholdModeChange}
					data={THRESHOLD_OPTIONS}
					rightSection={<Icon name="chevronDown" />}
					disabled={submitting}
				/>
				{(thresholdMode as string) === 'automatic' && (
					<>
						<Text size="sm" color="text/secondary/default" mb={4}>
							{AUTO_THRESHOLDS_MESSAGE}
						</Text>
						<Text
							size="sm"
							color="text/primary/default"
							weight="semibold"
							mb={4}
						>
							Threshold sensitivity
						</Text>
						<Box mx="md" mb="md">
							<Slider
								size="sm"
								w="100%"
								color="dark"
								label={null}
								showLabelOnHover={false}
								step={1}
								min={1}
								max={10}
								value={sensitivity}
								onChange={setSensitivity}
								marks={[
									{ value: 1, label: <Text>Low</Text> },
									{ value: 5, label: <Text>Default</Text> },
									{ value: 10, label: <Text>High</Text> },
								]}
								disabled={submitting}
							/>
						</Box>
						<Text size="sm" color="text/secondary/default">
							{SENSITIVITY_HELPER}
						</Text>
					</>
				)}
				{(thresholdMode as string) === 'manual' && (
					<SimpleGrid cols={2} spacing="md">
						<NumberInput
							label="Minimum"
							value={manualMin}
							onChange={handleManualMinChange}
							error={minError}
							disabled={submitting}
						/>
						<NumberInput
							label="Maximum"
							value={manualMax}
							onChange={handleManualMaxChange}
							error={maxError}
							disabled={submitting}
						/>
					</SimpleGrid>
				)}
				{(thresholdMode as string) === 'manual' &&
					manualMax === undefined &&
					manualMin === undefined && (
						<Text size="xs" color="text/critical/default">
							Please specify either a minimum or maximum threshold. If both are
							left unset, automatic thresholds will be applied.
						</Text>
					)}
				<Group position="right" mt="md" spacing={8}>
					<Button variant="default" onClick={onClose} disabled={submitting}>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleSubmit}
						loading={submitting}
						disabled={
							thresholdMode === 'manual' &&
							manualMax !== undefined &&
							manualMin !== undefined &&
							manualMax < manualMin
						}
					>
						{`Update ${monitors.length} Monitor${monitors.length === 1 ? '' : 's'}`}
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
